import React from 'react';

import { ErrorMessage } from './Animations';
import { cn } from './cn';
import {
  formDescriptionClassName,
  formErrorMessageClassName,
  formLabelClassName,
  formRequiredMarkClassName,
  formSuccessMessageClassName,
} from './formFieldStyles';

export interface FormFieldsetProps {
  /** Group label, rendered as a `<legend>` styled like a field label. */
  legend?: string;
  description?: string;
  errorMessage?: string;
  successMessage?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Accessible wrapper for a group of related controls (e.g. a radio group).
 * Uses native `<fieldset>` + `<legend>` so the group label is programmatically associated with the
 * controls — unlike `FormField`, whose `<label htmlFor>` only works for a single labelable control.
 */
export const FormFieldset = ({
  legend,
  description,
  errorMessage,
  successMessage,
  required,
  children,
  className,
}: FormFieldsetProps) => {
  return (
    // `display:flex` directly on a <fieldset> breaks <legend> layout in some browsers, so the flex
    // column lives on an inner <div> and the fieldset only resets its native border/padding/margin.
    <fieldset className={cn('m-0 border-none p-0', className)}>
      {legend && (
        <legend className={cn(formLabelClassName, 'mb-1.5 p-0')}>
          {legend}
          {required && <span className={formRequiredMarkClassName}>*</span>}
        </legend>
      )}
      <div className="flex flex-col gap-1.5">
        {description && <p className={formDescriptionClassName}>{description}</p>}
        {children}
        {errorMessage && <ErrorMessage className={formErrorMessageClassName}>{errorMessage}</ErrorMessage>}
        {successMessage && !errorMessage && (
          <ErrorMessage className={formSuccessMessageClassName}>{successMessage}</ErrorMessage>
        )}
      </div>
    </fieldset>
  );
};
